import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { formatFirebaseAuthError } from '../../utils/errorHandling';
import AuthLayout from './AuthLayout';

const VerifyEmailPage = () => {
    const { currentUser, sendVerificationEmail } = useAuth();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [errorKey, setErrorKey] = useState<string | null>(null);
    const [message, setMessage] = useState('');
    const [cooldown, setCooldown] = useState(true); // Start in cooldown mode
    const [cooldownTime, setCooldownTime] = useState(120); // 2 minutes initial cooldown

    // If user is already verified or not logged in, redirect appropriately
    if (!currentUser) {
        return <Navigate to="/login" replace />;
    }

    if (currentUser.emailVerified) {
        return <Navigate to="/" replace />;
    }

    // Start the cooldown timer immediately when component mounts
    useEffect(() => {
        // Initialize a countdown timer
        const interval = setInterval(() => {
            setCooldownTime((prevTime) => {
                if (prevTime <= 1) {
                    clearInterval(interval);
                    setCooldown(false);
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);

        // Cleanup the interval on component unmount
        return () => clearInterval(interval);
    }, []);

    const handleResendVerification = async () => {
        try {
            setErrorKey(null);
            setMessage('');
            setLoading(true);
            await sendVerificationEmail();
            setMessage(t('auth.verifyEmail.emailSent'));

            // Start cooldown to prevent rate limiting
            setCooldown(true);

            // Set a longer cooldown of 2 minutes to avoid Firebase rate limiting
            const cooldownSeconds = 120;
            setCooldownTime(cooldownSeconds);

            // Update the cooldown timer every second
            const interval = setInterval(() => {
                setCooldownTime((prevTime) => {
                    if (prevTime <= 1) {
                        clearInterval(interval);
                        setCooldown(false);
                        return 0;
                    }
                    return prevTime - 1;
                });
            }, 1000);
        } catch (err: any) {
            const formattedErrorKey = formatFirebaseAuthError(err);
            setErrorKey(formattedErrorKey);

            // If the error is related to too many requests, set a longer cooldown
            if (err.code === 'auth/too-many-requests') {
                setCooldown(true);
                // Set a long cooldown (5 minutes) when we hit rate limits
                const rateLimitCooldown = 300;
                setCooldownTime(rateLimitCooldown);

                // Update the cooldown timer every second
                const interval = setInterval(() => {
                    setCooldownTime((prevTime) => {
                        if (prevTime <= 1) {
                            clearInterval(interval);
                            setCooldown(false);
                            return 0;
                        }
                        return prevTime - 1;
                    });
                }, 1000);
            }

            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Format the cooldown time as minutes:seconds
    const formatCooldownTime = () => {
        const minutes = Math.floor(cooldownTime / 60);
        const seconds = cooldownTime % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    return (
        <AuthLayout title={t('auth.verifyEmail.title')}>
            {errorKey && (
                <Alert 
                    severity="error" 
                    sx={{
                        marginBottom: '24px',
                        borderRadius: '8px',
                    }} 
                    role="alert" 
                    aria-live="assertive"
                >
                    {t(errorKey)}
                </Alert>
            )}
            {message && (
                <Alert 
                    severity="success" 
                    sx={{
                        marginBottom: '24px',
                        borderRadius: '8px',
                    }} 
                    role="status" 
                    aria-live="polite"
                >
                    {message}
                </Alert>
            )}

            <Box sx={{ 
                width: '100%',
                marginTop: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
            }}>
                <Stack spacing={2}>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            fontWeight: 600,
                            color: '#FF6B00',
                            marginBottom: '16px',
                        }} 
                        color="primary"
                    >
                        {t('auth.verifyEmail.pleaseVerify')}
                    </Typography>

                    <Typography variant="body1" paragraph>
                        {t('auth.verifyEmail.sentTo')}{' '}
                        <Typography component="span" fontWeight="bold">
                            {currentUser.email}
                        </Typography>
                    </Typography>

                    <Box sx={{
                        marginBottom: '24px',
                        width: '100%',
                    }}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={handleResendVerification}
                            disabled={loading || cooldown}
                            sx={{
                                marginTop: '12px',
                                fontWeight: 600,
                                textTransform: 'none',
                                fontSize: '1rem',
                                backgroundColor: '#FF6B00',
                                color: '#ffffff',
                                height: '48px',
                                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                                '&:hover': {
                                    backgroundColor: '#FF7E33',
                                    boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.25)',
                                },
                                '&:disabled': {
                                    backgroundColor: 'rgba(255, 107, 0, 0.5)',
                                    color: 'rgba(255, 255, 255, 0.7)',
                                }
                            }}
                            aria-label={t('auth.verifyEmail.resendButtonAriaLabel')}
                        >
                            {loading ? (
                                <CircularProgress size={24} sx={{ color: '#ffffff' }} />
                            ) : cooldown ? (
                                t('auth.verifyEmail.resendAvailable', { time: formatCooldownTime() })
                            ) : (
                                t('auth.verifyEmail.resendButton')
                            )}
                        </Button>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        {t('auth.verifyEmail.alreadyVerified')}{' '}
                        <Box 
                            component="span" 
                            onClick={() => {
                                window.location.href = '/login';
                            }}
                            style={{ 
                                color: '#FF6B00', 
                                textDecoration: 'none',
                                fontWeight: 500,
                                cursor: 'pointer'
                            }}
                        >
                            {t('auth.verifyEmail.signInHere')}
                        </Box>
                    </Typography>
                </Stack>
            </Box>
        </AuthLayout>
    );
};

export default VerifyEmailPage;
